import { PGliteWorker } from '@electric-sql/pglite/worker'
import type { Folder, Conversation, Message, DocumentTimeline } from '~/types/database'

// 统一数据库接口类型 - 使用官方PGliteWorker多标签页模式
type DatabaseInstance = PGliteWorker

let db: DatabaseInstance | null = null
let initPromise: Promise<DatabaseInstance> | null = null

// 性能测量工具
const createDatabasePerformanceMeasure = (name: string) => {
  const startTime = performance.now()
  return {
    end: () => {
      const endTime = performance.now()
      const duration = endTime - startTime
      console.log(`💾 数据库性能 [${name}]: ${duration.toFixed(2)}ms`)
      return duration
    }
  }
}

export const useDatabase = () => {
  const initDatabase = async () => {
    // 如果已有初始化进行中，直接等待结果
    if (initPromise) {
      console.log('⏳ 检测到正在进行的初始化，等待完成...')
      return initPromise
    }

    // 如果数据库已初始化，直接返回
    if (db) {
      console.log('✅ 数据库已初始化，直接返回')
      return db
    }

    // 获取当前设置
    const settingsStore = useSettingsStore()
    // 确保设置已从 localStorage 加载
    settingsStore.loadSettings()
    const storageType = settingsStore.storageType
    const workerType = settingsStore.workerType
    
    console.log(`🔧 初始化${storageType.toUpperCase()}数据库 (${workerType} worker模式)`)
    const totalMeasure = createDatabasePerformanceMeasure('完整数据库初始化')
    
    // 开始实际初始化，记录 Promise 以避免并发
    initPromise = (async () => {
      try {
        // 使用官方 PGliteWorker 多标签页模式
        const workerMeasure = createDatabasePerformanceMeasure('创建Worker实例')
        
        // 创建worker实例，添加多标签页支持的唯一ID
        const workerId = `pglite-worker-${storageType}`
        const worker = new Worker(
          new URL('../workers/database.worker.ts', import.meta.url),
          { 
            type: 'module',
            name: workerId
          }
        )
        workerMeasure.end()
        
        const pgliteMeasure = createDatabasePerformanceMeasure('创建PGliteWorker')
        // 使用官方PGliteWorker，它内置了多标签页支持和自动选举机制
        db = new PGliteWorker(worker, {
          storageType: storageType,
          relaxedDurability: true,
          // 为多标签页设置唯一ID，相同ID的worker会共享数据库实例
          id: `database-${storageType}`
        } as any)
        pgliteMeasure.end()
        
        // 等待worker初始化完成 - 使用更智能的等待机制
        const waitMeasure = createDatabasePerformanceMeasure('等待Worker初始化')
        let retries = 0
        const maxRetries = 50 // 最多等待5秒（50 * 100ms）
        
        while (retries < maxRetries) {
          try {
            // 尝试执行一个简单的查询来测试连接
            await db.query('SELECT 1')
            break
          } catch (error) {
            // 如果还没准备好，等待100ms后重试
            await new Promise(resolve => setTimeout(resolve, 100))
            retries++
          }
        }
        
        if (retries >= maxRetries) {
          throw new Error('Worker初始化超时')
        }
        
        waitMeasure.end()
        console.log(`✅ ${storageType.toUpperCase()}数据库初始化成功 (PGliteWorker多标签页模式)`)
        
        totalMeasure.end()
        return db
      } catch (error) {
        totalMeasure.end()
        console.error(`❌ ${storageType.toUpperCase()}数据库初始化失败:`, error)
        throw error
      }
    })()

    try {
      const result = await initPromise
      return result
    } finally {
      // 初始化完成后清空 Promise，无论成功或失败
      initPromise = null
    }
  }

  const getFolders = async (): Promise<Folder[]> => {
    if (!db) await initDatabase()
    const result = await db!.query(`
      SELECT * FROM folders 
      ORDER BY created_at ASC
    `)
    return result.rows as Folder[]
  }

  const createFolder = async (name: string, parentId?: string): Promise<Folder> => {
    if (!db) await initDatabase()
    
    // 生成UUID和时间戳
    const id = crypto.randomUUID()
    const now = new Date().toISOString()
    const defaultUserId = '00000000-0000-0000-0000-000000000000'
    
    const result = await db!.query(`
      INSERT INTO folders (id, user_id, name, parent_id, created_at, updated_at, is_expanded) 
      VALUES ($1, $2, $3, $4, $5, $6, $7) 
      RETURNING *
    `, [id, defaultUserId, name, parentId || null, now, now, true])
    return result.rows[0] as Folder
  }

  const updateFolder = async (id: string, updates: Partial<Folder>): Promise<Folder> => {
    if (!db) await initDatabase()
    
    // 添加更新时间
    const processedUpdates = { 
      ...updates, 
      updated_at: new Date().toISOString() 
    }
    
    const setClause = Object.keys(processedUpdates)
      .filter(key => key !== 'id')
      .map((key, index) => `${key} = $${index + 2}`)
      .join(', ')
    
    const values = [id, ...Object.values(processedUpdates).filter((_, index) => Object.keys(processedUpdates)[index] !== 'id')]
    
    const result = await db!.query(`
      UPDATE folders 
      SET ${setClause}
      WHERE id = $1 
      RETURNING *
    `, values)
    return result.rows[0] as Folder
  }

  const deleteFolder = async (id: string): Promise<void> => {
    if (!db) await initDatabase()
    await db!.query('DELETE FROM folders WHERE id = $1', [id])
  }

  const updateFolderExpandedState = async (id: string, isExpanded: boolean): Promise<void> => {
    if (!db) await initDatabase()
    await db!.query(`
      UPDATE folders 
      SET is_expanded = $2, updated_at = $3
      WHERE id = $1
    `, [id, isExpanded, new Date().toISOString()])
  }

  const getConversations = async (folderId?: string): Promise<Conversation[]> => {
    if (!db) await initDatabase()
    let query = 'SELECT * FROM conversations'
    let params: any[] = []
    
    if (folderId) {
      query += ' WHERE folder_id = $1'
      params = [folderId]
    }
    
    query += ' ORDER BY updated_at DESC'
    
    const result = await db!.query(query, params)
    const conversations = result.rows as any[]
    
    // 解析 messages JSON 并映射字段
    return conversations.map((conv: any) => ({
      ...conv,
      messages: typeof conv.messages === 'string' ? JSON.parse(conv.messages) : conv.messages || [],
      // 映射 snake_case 数据库字段到 camelCase TypeScript 字段
      wordMode: conv.word_mode || false,
      documentMessageId: conv.document_message_id || undefined
    })) as Conversation[]
  }

  const createConversation = async (title: string, folderId?: string): Promise<Conversation> => {
    if (!db) await initDatabase()
    
    // 生成UUID和时间戳
    const id = crypto.randomUUID()
    const now = new Date().toISOString()
    const defaultUserId = '00000000-0000-0000-0000-000000000000'
    
    const result = await db!.query(`
      INSERT INTO conversations (id, user_id, title, folder_id, messages, created_at, updated_at) 
      VALUES ($1, $2, $3, $4, $5, $6, $7) 
      RETURNING *
    `, [id, defaultUserId, title, folderId || null, JSON.stringify([]), now, now])
    
    const conversation = result.rows[0] as any
    return {
      ...conversation,
      messages: typeof conversation.messages === 'string' ? JSON.parse(conversation.messages) : conversation.messages || []
    } as Conversation
  }

  const updateConversation = async (id: string, updates: Partial<Conversation>): Promise<Conversation> => {
    if (!db) await initDatabase()
    
    // 处理 messages 字段的 JSON 序列化和添加更新时间
    const processedUpdates: any = { 
      ...updates,
      updated_at: new Date().toISOString()
    }
    
    // 映射 camelCase TypeScript 字段到 snake_case 数据库字段
    if ('wordMode' in processedUpdates) {
      processedUpdates.word_mode = processedUpdates.wordMode
      delete processedUpdates.wordMode
    }
    if ('documentMessageId' in processedUpdates) {
      processedUpdates.document_message_id = processedUpdates.documentMessageId
      delete processedUpdates.documentMessageId
    }
    
    if (processedUpdates.messages) {
      processedUpdates.messages = JSON.stringify(processedUpdates.messages)
    }
    
    const setClause = Object.keys(processedUpdates)
      .filter(key => key !== 'id')
      .map((key, index) => `${key} = $${index + 2}`)
      .join(', ')
    
    const values = [id, ...Object.values(processedUpdates).filter((_, index) => Object.keys(processedUpdates)[index] !== 'id')]
    
    const result = await db!.query(`
      UPDATE conversations 
      SET ${setClause}
      WHERE id = $1 
      RETURNING *
    `, values)
    
    const conversation = result.rows[0] as any
    
    if (!conversation) {
      throw new Error(`Conversation with id ${id} not found`)
    }
    
    // 解析 messages JSON 并映射字段
    if (conversation.messages && typeof conversation.messages === 'string') {
      conversation.messages = JSON.parse(conversation.messages)
    } else if (!conversation.messages) {
      conversation.messages = []
    }
    
    // 映射 snake_case 数据库字段到 camelCase TypeScript 字段
    conversation.wordMode = conversation.word_mode || false
    conversation.documentMessageId = conversation.document_message_id || undefined
    
    return conversation as Conversation
  }

  const deleteConversation = async (id: string): Promise<void> => {
    if (!db) await initDatabase()
    await db!.query('DELETE FROM conversations WHERE id = $1', [id])
  }

  const getConversation = async (id: string): Promise<Conversation | null> => {
    if (!db) await initDatabase()
    const result = await db!.query('SELECT * FROM conversations WHERE id = $1', [id])
    const conversation = result.rows[0] as any
    
    if (!conversation) {
      return null
    }
    
    // 解析 messages JSON 并确保数据完整性
    const parsedConversation = {
      ...conversation,
      messages: typeof conversation.messages === 'string' ? JSON.parse(conversation.messages) : conversation.messages || [],
      // 映射 snake_case 数据库字段到 camelCase TypeScript 字段
      wordMode: conversation.word_mode || false,
      documentMessageId: conversation.document_message_id || undefined
    } as Conversation
    
    // 确保基本字段存在
    if (!parsedConversation.title) {
      parsedConversation.title = `对话 ${id.slice(0, 8)}`
    }
    
    return parsedConversation
  }

  const searchConversations = async (searchTerm: string): Promise<Conversation[]> => {
    if (!db) await initDatabase()
    
    if (!searchTerm.trim()) {
      return await getConversations()
    }
    
    const measure = createDatabasePerformanceMeasure('全文搜索')
    
    try {
      // 使用 ILIKE 进行大小写不敏感的搜索
      // 搜索标题和消息内容
      const result = await db!.query(`
        SELECT * FROM conversations 
        WHERE title ILIKE $1 
        OR messages::text ILIKE $1
        ORDER BY updated_at DESC
      `, [`%${searchTerm}%`])
      
      const conversations = result.rows as any[]
      
      // 解析 messages JSON 并进一步过滤
      const parsedConversations = conversations.map((conv: any) => ({
        ...conv,
        messages: typeof conv.messages === 'string' ? JSON.parse(conv.messages) : conv.messages || []
      })) as Conversation[]
      
      // 在应用层进行更精确的搜索，确保搜索词真正出现在消息内容中
      const filteredConversations = parsedConversations.filter(conv => {
        // 检查标题
        if (conv.title.toLowerCase().includes(searchTerm.toLowerCase())) {
          return true
        }
        
        // 检查消息内容
        return conv.messages.some((msg: Message) => 
          msg.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (msg.thinking && msg.thinking.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      })
      
      measure.end()
      return filteredConversations
    } catch (error) {
      measure.end()
      console.error('搜索对话失败:', error)
      throw error
    }
  }

  // 简单的字符串哈希函数
  const simpleHash = (str: string): string => {
    let hash = 0
    if (str.length === 0) return hash.toString()
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // 转换为32位整数
    }
    return Math.abs(hash).toString(16)
  }

  // 创建文档时间线记录
  const createDocumentTimelineEntry = async (
    conversationId: string, 
    content: string, 
    changeSummary?: string
  ): Promise<DocumentTimeline> => {
    if (!db) await initDatabase()
    
    const id = crypto.randomUUID()
    const now = new Date().toISOString()
    const defaultUserId = '00000000-0000-0000-0000-000000000000'
    const contentHash = simpleHash(content)
    
    // 检查是否已存在相同内容哈希的记录（避免重复保存相同内容）
    const existingResult = await db!.query(`
      SELECT id FROM document_timeline 
      WHERE conversation_id = $1 AND content_hash = $2 
      ORDER BY created_at DESC LIMIT 1
    `, [conversationId, contentHash])
    
    if (existingResult.rows.length > 0) {
      // 如果内容没有变化，不创建新的时间线记录
      return await getDocumentTimelineEntry(existingResult.rows[0].id as string)
    }
    
    const result = await db!.query(`
      INSERT INTO document_timeline (id, conversation_id, user_id, content, content_hash, created_at, change_summary) 
      VALUES ($1, $2, $3, $4, $5, $6, $7) 
      RETURNING *
    `, [id, conversationId, defaultUserId, content, contentHash, now, changeSummary || null])
    
    return result.rows[0] as DocumentTimeline
  }

  // 获取文档时间线
  const getDocumentTimeline = async (conversationId: string, limit: number = 50): Promise<DocumentTimeline[]> => {
    if (!db) await initDatabase()
    
    const result = await db!.query(`
      SELECT * FROM document_timeline 
      WHERE conversation_id = $1 
      ORDER BY created_at DESC 
      LIMIT $2
    `, [conversationId, limit])
    
    return result.rows as DocumentTimeline[]
  }

  // 获取单个时间线记录
  const getDocumentTimelineEntry = async (id: string): Promise<DocumentTimeline> => {
    if (!db) await initDatabase()
    
    const result = await db!.query(`
      SELECT * FROM document_timeline WHERE id = $1
    `, [id])
    
    if (result.rows.length === 0) {
      throw new Error('时间线记录不存在')
    }
    
    return result.rows[0] as DocumentTimeline
  }

  // 删除时间线记录
  const deleteDocumentTimelineEntry = async (id: string): Promise<void> => {
    if (!db) await initDatabase()
    await db!.query('DELETE FROM document_timeline WHERE id = $1', [id])
  }

  // 清理旧的时间线记录（保留最近的指定数量）
  const cleanupDocumentTimeline = async (conversationId: string, keepCount: number = 100): Promise<void> => {
    if (!db) await initDatabase()
    
    await db!.query(`
      DELETE FROM document_timeline 
      WHERE conversation_id = $1 
      AND id NOT IN (
        SELECT id FROM document_timeline 
        WHERE conversation_id = $1 
        ORDER BY created_at DESC 
        LIMIT $2
      )
    `, [conversationId, keepCount])
  }

  const closeDatabase = async () => {
    if (db) {
      // PGliteWorker会自动处理清理
      db = null
      initPromise = null
      console.log('数据库连接已关闭')
    }
  }

  // 添加页面卸载时的清理
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      closeDatabase().catch(console.error)
    })
  }

  return {
    initDatabase,
    closeDatabase,
    getFolders,
    createFolder,
    updateFolder,
    deleteFolder,
    updateFolderExpandedState,
    getConversations,
    createConversation,
    updateConversation,
    deleteConversation,
    getConversation,
    searchConversations,
    // 文档时间线相关函数
    createDocumentTimelineEntry,
    getDocumentTimeline,
    getDocumentTimelineEntry,
    deleteDocumentTimelineEntry,
    cleanupDocumentTimeline
  }
}